import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/joy';
import { MdOutlineAssignment } from 'react-icons/md';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { listProjectJobComments } from './jobCard.service';

function formatStamp(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/London',
  });
}

type JobCardCommentRow = {
  id: string;
  body: string;
  createdAt: string;
  authorName: string;
  cardTitle: string;
};

const JobCardCommentsCard: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [comments, setComments] = useState<JobCardCommentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const rows = await listProjectJobComments(projectId);
    setComments(rows.map((row) => ({
      id: row.id,
      body: row.body,
      createdAt: row.created_at,
      authorName: row.author_name || 'Staff',
      cardTitle: row.cardTitle || 'Job card',
    })));
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reload()
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load job card comments'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdOutlineAssignment}
          title="Job card comments"
          description="Comments written on the job cards. Change them on the card itself."
        />
        {error && <Alert color="danger" sx={{ mb: 1 }}>{error}</Alert>}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size="sm" /></Stack>
        ) : (
          <Stack gap={1.25}>
            {comments.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No job card comments yet.</Typography>
            )}
            {comments.map((comment) => (
              <Card key={comment.id} variant="soft" color="neutral" sx={{ width: '100%', boxShadow: 'none' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Typography level="title-sm">{comment.authorName}</Typography>
                  <Chip size="sm" variant="outlined">{comment.cardTitle}</Chip>
                </Stack>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
                  {formatStamp(comment.createdAt)}
                  {' · From the job card'}
                </Typography>
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                  {comment.body}
                </Typography>
              </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default JobCardCommentsCard;
